/**
 * A Server-Sent Events parser, as a pure function over text.
 *
 * The browser's own `EventSource` cannot be used for the streaming send: it only issues
 * `GET` and cannot carry a body, and the message text belongs in a body rather than a
 * query string, where it would land in access logs and browser history — which for a
 * workspace with a sensitive-data filter in front of it would defeat the point of having
 * one. So the stream is read from `fetch`, and the wire format has to be parsed here.
 *
 * Losing `EventSource` also loses its automatic reconnect, which is the outcome we want.
 * There is no stream reattachment server-side — the pod holding the connection is the pod
 * talking to the provider — so a reconnect would silently start a *second* generation and
 * bill for it rather than rejoining the first. A refresh polls instead.
 *
 * This file is deliberately free of `fetch`, Vue and component state so the format
 * handling is testable on its own; `sse.spec.js` covers it.
 */

/**
 * A parser you feed decoded text chunks to.
 *
 * Chunk boundaries fall wherever the network puts them, which is routinely mid-line and
 * occasionally between the two halves of a `\r\n`. Everything below exists to make those
 * boundaries invisible to the caller.
 *
 * @param {Function} onEvent - called with { event, data } for each complete event
 * @returns {Object} { feed, flush }
 */
export function createSseParser(onEvent) {
  let buffer = "";
  let eventName = "";
  let dataLines = [];

  /**
   * A blank line ends an event.
   *
   * An event carrying no `data` line is not dispatched — that is the specification, and
   * it is also what keeps a stray blank line from being reported as an empty message.
   */
  function dispatch() {
    if (!dataLines.length) {
      eventName = "";
      return;
    }

    const event = eventName || "message";
    const data = dataLines.join("\n");

    eventName = "";
    dataLines = [];

    onEvent({ event, data });
  }

  function handleLine(line) {
    if (line === "") {
      dispatch();
      return;
    }

    // A line beginning with a colon is a comment. The backend sends `: keep-alive` every
    // fifteen seconds, because a model can spend a long time reading the conversation
    // before its first token and nginx counts silence, not connection age, against its
    // read timeout.
    if (line.charAt(0) === ":") {
      return;
    }

    const colon = line.indexOf(":");
    let field = line;
    let value = "";

    if (colon !== -1) {
      field = line.slice(0, colon);
      value = line.slice(colon + 1);
      // Exactly one leading space is part of the framing, not the value.
      if (value.charAt(0) === " ") {
        value = value.slice(1);
      }
    }

    if (field === "event") {
      eventName = value;
    } else if (field === "data") {
      dataLines.push(value);
    }
    // `id` and `retry` are ignored on purpose: both exist to serve reconnection, and
    // reconnecting to this endpoint would start a second generation.
  }

  return {
    /**
     * Take the next piece of decoded text.
     *
     * @param {String} chunk
     */
    feed(chunk) {
      buffer += chunk;

      for (;;) {
        const index = lineBreakIndex(buffer);
        if (index === -1) {
          break;
        }

        // A carriage return at the very end of the buffer may be the first half of a
        // `\r\n` whose second half is still in flight. Treating it as a line ending now
        // would split one event across two, so it waits for the next chunk.
        if (buffer.charAt(index) === "\r" && index === buffer.length - 1) {
          break;
        }

        const line = buffer.slice(0, index);
        const skip = buffer.charAt(index) === "\r" && buffer.charAt(index + 1) === "\n" ? 2 : 1;
        buffer = buffer.slice(index + skip);

        handleLine(line);
      }
    },

    /**
     * The stream ended.
     *
     * Anything left in the buffer is an event the server never finished sending, and it
     * is discarded rather than guessed at — a half-arrived `data:` line is not JSON, and
     * a truncated stream is already reported by the caller as the failure it is.
     */
    flush() {
      buffer = "";
      eventName = "";
      dataLines = [];
    },
  };
}

/** The first `\n` or `\r` in the buffer, or -1. */
function lineBreakIndex(text) {
  for (let i = 0; i < text.length; i += 1) {
    const character = text.charAt(i);
    if (character === "\n" || character === "\r") {
      return i;
    }
  }
  return -1;
}

/**
 * Parse an event's `data` payload.
 *
 * Returns null rather than throwing on malformed JSON. A single unreadable frame in the
 * middle of an answer is not worth tearing the stream down for — the caller skips it and
 * keeps reading, and the `done` event still carries the authoritative finished message.
 *
 * @param {String} data
 * @returns {Object|null}
 */
export function parseEventData(data) {
  try {
    return JSON.parse(data);
  } catch (error) {
    return null;
  }
}
